import React, { useState } from 'react';

import useIntersect from '../../../hooks/useIntersect';
import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { makeClassNames } from '../../../utils/styles';
import ReportTabsPanel from '../../molecules/report-tabs-panel/report-tabs-panel';
import TeamsTabs, { getTeamFormation } from '../../molecules/teams-tabs/teams-tabs';
import { IMatchFormationsDataTeam } from '../match-formations/match-formations';
import Glider from '../../organisms/glider/glider';

import { createIds, getCta, getGliderItems, getLmpCta, getRound } from './predicted-lineups-helpers';

import './predicted-lineups-hp.scss';

export type PredictedLineupVariant = 'PREDICTED-LINEUP' | 'LMP';
export const PREDICTEDLINEUP: PredictedLineupVariant = 'PREDICTED-LINEUP';
export const LMP: PredictedLineupVariant = 'LMP';

export interface ITeamPredictedLineup extends Omit<IMatchFormationsDataTeam, 'referees'> {
  date: string;
  ctaUrlAway?: string | null;
  ctaUrlHome?: string | null;
  ctaLmpUrl?: string | null; // ISS-6463 - LMP PREDICTED LINEUPS
}

export interface IPredictedLineupsHp extends IComponent {
  competitionId?: number; // BE sends this, but it's actually unused in DS (will be MANDATORY)
  matchList: ITeamPredictedLineup[];
  round: string;
  variant?: PredictedLineupVariant;
}

const classNames = () =>
  makeClassNames({
    predictedLineupsHpWrapper: 'predicted-lineups-hp-wrapper',
    predictedLineupsHp: 'predicted-lineups-hp',
    ctaWrapper: 'predicted-lineups-hp__cta-wrapper',
    ctaHome: 'predicted-lineups-hp__cta-home',
    ctaAway: 'predicted-lineups-hp__cta-away',
    ctaLmp: 'predicted-lineups-hp__cta-lmp',
    titleLmp: 'predicted-lineups-hp__title-lmp',
    header: 'predicted-lineups-hp__header',
    matchDate: 'predicted-lineups-hp__match-date',
    dateShort: 'predicted-lineups-hp__date-short',
    dateLong: 'predicted-lineups-hp__date-long',
    formation: 'predicted-lineups-hp__formation',
    nav: 'predicted-lineups-hp__nav',
    navList: 'predicted-lineups-hp__nav-list',
    navItem: 'predicted-lineups-hp__nav-item',
    navArrowLeft: 'predicted-lineups-hp__nav-arrow-left',
    navArrowRight: 'predicted-lineups-hp__nav-arrow-right',
  });

const PredictedLineupsHp = ({
  /* istanbul ignore next: default value */ variant = PREDICTEDLINEUP,
  matchList,
  round,
  trackingData,
}: IPredictedLineupsHp) => {
  const cn = classNames();

  const gliderItems = getGliderItems(matchList, variant);

  const [activeIndex, setActiveIndex] = useState<number>(0);

  const onIntersect = (item: HTMLElement) => {
    setActiveIndex(Number(item.dataset.index));
  };
  useIntersect('[data-intersect]', onIntersect);

  const onSelectedGliderItem = (gliderIndex: number) => {
    if (gliderIndex !== activeIndex) {
      setActiveIndex(gliderIndex);
    }
  };

  const teamsTabsProps = {
    hasInput: true,
    roundLabel: t('match.statistics.label.vs'),
  };

  // FIXME: if PREDICTED LINEUP in LMP TAB won't be a NEW WIDGET passing "LMP" as VARIANT, we could COMPUTE VARIANT based on MATCH LIST LENGTH
  // const variant: PredictedLineupVariant = matchList.length > 1 ? PREDICTEDLINEUP : LMP;

  return (
    <div className={cn.predictedLineupsHpWrapper}>
      {variant !== 'LMP' ? (
        <Glider activeIndex={activeIndex} items={gliderItems} onSelectedGliderItem={onSelectedGliderItem} />
      ) : null}
      {getRound(variant, round, cn)}
      {matchList.map((match, index) => {
        const { home, away } = match;
        const ids = createIds(home, away, index);

        return (
          <div id={ids.full} key={ids.full} data-intersect className={cn.predictedLineupsHp}>
            {getLmpCta(match, variant, cn, trackingData)}
            <TeamsTabs home={home} away={away} idHome={home.name} idAway={away.name} hasCta {...teamsTabsProps} />
            <div className={cn.ctaWrapper} data-panels="true">
              <div data-panel-left>
                <div className={cn.ctaHome}>
                  <div className={cn.formation}>
                    {home.formation && getTeamFormation(home.formation, 'formation-home', '16--14', '400', 'blue')}
                  </div>
                  {getCta(home.ctaUrl, variant)}
                </div>
              </div>
              <div data-panel-right>
                <div className={cn.ctaAway}>
                  <div className={cn.formation}>
                    {away.formation &&
                      getTeamFormation(away.formation, 'formation-away', '16--14', '400', 'blue-light')}
                  </div>
                  {getCta(away.ctaUrl, variant)}
                </div>
              </div>
            </div>
            <ReportTabsPanel away={away} home={home} panelVariant={PREDICTEDLINEUP} status={match.status} />
          </div>
        );
      })}
    </div>
  );
};

export default PredictedLineupsHp;
